import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import queryString from "query-string";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import {
  UncontrolledDropdown,
  DropdownMenu,
  DropdownToggle,
  Card,
  DropdownItem,
  Col,
  Label,
  Row,
  Badge,
} from "reactstrap";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Button,
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  PaginationComponent,
  RSelect
} from "../../components/Component";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import { fetchDeals } from "../../redux/actions/deal";
import { DealCategory, DealStatus, newRoles } from "../../utils/envConfig";
import LoadingComponent from "../components/loader/tableLoader";
import SelectAgent from "../../components/SelectAgent";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import LoaderModal from "../../components/modals/LoaderModal";
import { showToast } from "../../utils/toast/toast";
import { useMutation } from "react-query";
import dealQueries from "../../queries/dealQueries";
import { ConvertDealProvider } from "./components/convert-to-listing/modal/ConvertDealContext";
import qs from "query-string";
import SearchClientSelect from "../../components/SearchClientSelect";

// Add custom styles to ensure dropdown menus are visible and have proper focus/selection styles




const EOIDealList = ({
  url = "/list-eoi-deals",
  agent = null,
  user = null,
  showAgentFilter = true,
}) => {
  const authUser = useSelector((state) => state?.user?.loggedInUser);
  const location = useLocation();
  const navigate = useNavigate();
  const axios = useAxiosPrivate();
  const dispatch = useDispatch();

  const { agent: chosenAgent } = location.state || {};

  const { loading, deals: allDeals } = useSelector((state) => state.deal);

  const query = queryString.parse(location.search);

  const dealStatusOptions = Object.values(DealStatus).map((item) => ({
    label: item,
    value: item,
  }));
  const agents = useSelector((state) => state?.user?.agents);
  const loggedInAgent = agents?.find((agent) => agent?.userId === authUser.id)
  showAgentFilter = ![newRoles.Agent, newRoles.AnonymousAgent].includes(authUser.role)

  const [page, setPage] = useState(query.page ? parseInt(query.page) : 1);
  const [perPage, setPerPage] = useState(
    query.per_page ? parseInt(query.per_page) : 10
  );
  const [status, setStatus] = useState(query.status || "");
  const [searchText, setSearchText] = useState(query.search || "");
  const [selectedAgent, setSelectedAgent] = useState(
    showAgentFilter ? loggedInAgent?.id :
      chosenAgent ? chosenAgent : agent ? agent : null
  );
  const [selectedDeveloper, setSelectedDeveloper] = useState(query.developer_id ? { value: query.developer_id, label: query.developer_name || "Developer" } : null);
  const [selectedClient, setSelectedClient] = useState(query.client_id ? { value: query.client_id, label: query.client_name || "Client" } : null);
  const [fromDate, setFromDate] = useState(
    query.from_date ? new Date(query.from_date) : null
  );
  const [toDate, setToDate] = useState(
    query.to_date ? new Date(query.to_date) : null
  );
  const [filCount, setFilCount] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showLoaderModal, setShowLoaderModal] = useState(false);

  // Fetch developers for filter
  const [developerOptions, setDeveloperOptions] = useState([]);
  const [loadingDevelopers, setLoadingDevelopers] = useState(false);

  // Fetch offplan projects for filter
  const [selectedOffplan, setSelectedOffplan] = useState(query.offplan_id ? { value: query.offplan_id, label: query.offplan_name || "Project" } : null);
  const [offplanOptions, setOffplanOptions] = useState([]);
  const [loadingOffplans, setLoadingOffplans] = useState(false);

  // Fetch developers
  useEffect(() => {
    const fetchDevelopers = async () => {
      try {
        setLoadingDevelopers(true);
        const { data } = await axios.get('/off-plan/plan-developer');
        if (data) {
          setDeveloperOptions(
            data.map((dev) => ({
              ...dev,
              value: dev._id || dev.id,
              label: dev.name
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching developers:", err);
      } finally {
        setLoadingDevelopers(false);
      }
    };

    fetchDevelopers();
  }, [axios]);

  // Fetch offplan projects
  useEffect(() => {
    const fetchOffplans = async () => {
      try {
        setLoadingOffplans(true);
        // If developer is selected, filter offplans by developer
        const baseUrl = '/off-plan?per_page=100';
        const developerQuery = selectedDeveloper ? `&developer=${selectedDeveloper.value}` : '';
        const { data } = await axios.get(`${baseUrl}${developerQuery}`);

        if (data && data.filteredOutput) {
          setOffplanOptions(
            data.filteredOutput.map((project) => ({
              ...project,
              value: project._id || project.id,
              label: project.developmentName
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching offplan projects:", err);
      } finally {
        setLoadingOffplans(false);
      }
    };

    fetchOffplans();
  }, [axios, selectedDeveloper]);

  // CSV generation mutation
  const csvMutation = useMutation(
    (dealParams) => dealQueries.generateCsvDeals(axios, { dealParams }),
    {
      onSuccess: (data) => {
        setShowLoaderModal(false);
        if (data?.url) {
          window.open(data.url, "_blank");
        }
      },
      onError: (error) => {
        setShowLoaderModal(false);
        showToast({
          text: "Failed to generate CSV",
          type: "error",
        });
      },
    }
  );


  // Function to query deals
  const queryDeals = useCallback(
    (params = {}) => {

      const queryParams = {
        page: params.page || page,
        per_page: params.per_page || perPage,
        status: params.status || status,
        search: params.search || searchText,
        agent: params.agent_id || selectedAgent?.id || selectedAgent?._id,
        user_id: params.client_id || selectedClient?.value || selectedClient?.id || selectedClient?._id,
        developer_id: params.developer_id || selectedDeveloper?.value,
        developer_name: selectedDeveloper?.label,
        offplan_id: params.offplan_id || selectedOffplan?.value,
        offplan_name: selectedOffplan?.label,
        client_id: params.client_id || selectedClient?.value || selectedClient?.id || selectedClient?._id,
        client_name: selectedClient?.label,
        from_date: params.from_date
          ? params.from_date
          : fromDate
            ? fromDate.toISOString()
            : "",
        to_date: params.to_date
          ? params.to_date
          : toDate
            ? toDate.toISOString()
            : "",
        category: DealCategory.EOI,
      };



      // Update URL with query params
      const searchParams = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value) {
          searchParams.append(key, value);
        }
      });

      navigate({
        pathname: location.pathname,
        search: searchParams.toString(),
      });

      delete queryParams.client_id;
      delete queryParams.client_name;
      delete queryParams.developer_name;
      delete queryParams.offplan_name;

      dispatch(fetchDeals(axios, `?${qs.stringify(queryParams)}`));
    },
    [
      page,
      perPage,
      status,
      searchText,
      selectedAgent,
      selectedDeveloper,
      selectedClient,
      selectedOffplan,
      fromDate,
      toDate,
      dispatch,
      navigate,
      location.pathname,
      axios,
    ]
  );

  // Debounced search function
  const debouncedSearch = useMemo(() =>
    debounce((value) => {
      queryDeals({ search: value, page: 1 });
    }, 500),
    [queryDeals]
  );

  // Handle search input change
  const onSearchChange = (e) => {
    const { value } = e.target;
    setSearchText(value);
    debouncedSearch(value);
  };

  // Handle status change
  const onStatusChange = (selectedOption) => {
    setStatus(selectedOption?.value || "");
    queryDeals({ status: selectedOption?.value || "", page: 1 });
  };

  // Handle agent change
  const onAgentChange = (agent) => {
    console.log("Agent selected:", agent);
    setSelectedAgent(agent);
    queryDeals({
      agent_id: agent?.id || agent?._id,
      page: 1,
    });
  };

  // Handle developer change
  const onDeveloperChange = (developer) => {
    console.log("Developer selected:", developer);
    setSelectedDeveloper(developer);
    queryDeals({
      developer_id: developer?.value,
      page: 1,
    });
  };

  // Handle client change
  const onClientChange = (client) => {
    console.log("Client selected:", client);
    setSelectedClient(client);
    queryDeals({
      client_id: client?.id || client?._id,
      page: 1,
    });
  };

  // Handle offplan (project) change
  const onOffplanChange = (offplan) => {
    console.log("Project selected:", offplan);
    setSelectedOffplan(offplan);
    queryDeals({
      offplan_id: offplan?.value,
      page: 1,
    });
  };

  // Handle from date change
  const onFromDateChange = (date) => {
    setFromDate(date);
    queryDeals({ from_date: date ? date.toISOString() : "", page: 1 });
  };

  // Handle to date change
  const onToDateChange = (date) => {
    setToDate(date);
    queryDeals({ to_date: date ? date.toISOString() : "", page: 1 });
  };

  // Handle reset filters
  const resetFilters = () => {
    setStatus("");
    setSearchText("");
    setSelectedAgent(null);
    setSelectedDeveloper(null);
    setSelectedOffplan(null);
    setSelectedClient(null);
    setFromDate(null);
    setToDate(null);
    queryDeals({
      status: "",
      search: "",
      agent_id: "",
      developer_id: "",
      offplan_id: "",
      client_id: "",
      from_date: "",
      to_date: "",
      page: 1,
    });
  };

  // Update filtered count when deals data changes
  useEffect(() => {
    if (allDeals?.filteredCount) {
      setFilCount(allDeals.filteredCount);
    }
  }, [allDeals]);

  // Initial fetch of deals
  useEffect(() => {
    queryDeals();
  }, [queryDeals]);

  const deniedPermissions = useMemo(() => {
    if (authUser) {
      return authUser?.specialPermissions?.deny || {};
    }
  }, [authUser]);

  const checkIfLeadsBelongsToAuthUser = (deal) => {
    if (deal.category === DealCategory.SecondaryProperty) {
      return deal?.buyerAssignedAgent?.userId === authUser?.id || deal?.sellerAssignedAgent?.userId === authUser?.id;
    }
    if (deal.category === DealCategory.PrimaryOffplan) {
      return deal?.assignedAgent === authUser?.id;
    }
    return deal?.assignedAgent?.userId === authUser?.id;
  }

  return (
    <React.Fragment>
      <Head title="EOI Deals" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Expression of Interest Deals</BlockTitle>
              <BlockDes className="text-soft">
                <p>You have total {filCount} EOI deals</p>
              </BlockDes>

              {/* Search Component */}
              <div className="mt-4" style={{ width: '100%', maxWidth: '800px', minWidth: '450px' }}>
                <div className="form-control-wrap d-flex">
                  <div className="form-icon form-icon-left">
                    <Icon name="search"></Icon>
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="search-input"
                    placeholder="Search by deal ID, client name, or agent"
                    value={searchText}
                    onChange={onSearchChange}
                  />
                  {(searchText || status || selectedAgent || selectedDeveloper || selectedClient || fromDate || toDate) && (
                    <Button
                      color="primary"
                      size="md"
                      className="ml-2"
                      onClick={resetFilters}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand mr-n1 ${false ? "active" : ""
                    }`}
                  onClick={() => { }}
                >
                  <Icon name="menu-alt-r"></Icon>
                </Button>
                <div className="toggle-expand-content">
                  <ul className="nk-block-tools g-3">
                    {/* <li>
                      <Button
                        color="primary"
                        className="btn-dim"
                        onClick={handleGenerateCSV}
                      >
                        <Icon name="download-cloud"></Icon>
                        <span>Export CSV</span>
                      </Button>
                    </li> */}
                    <li>
                      <Button
                        color="primary"
                        onClick={() => navigate("/add-deal")}
                      >
                        <Icon name="plus"></Icon>
                        <span>Add EOI Deal</span>
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered card-stretch">
            <div className="card-inner-group">
              <div className="card-inner">
                <div className="card-title-group" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className="card-title">
                    <h5 className="title">All EOI Deals</h5>
                  </div>
                  <div className="card-tools mr-n1">
                    <ul className="btn-toolbar">
                      <li>
                        <Button
                          onClick={resetFilters}
                          className="btn-icon search-toggle toggle-search"
                        >
                          <Icon name="undo"></Icon>
                        </Button>
                      </li>
                      <li className="btn-toolbar-sep"></li>
                      <li>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            tag="a"
                            className="btn btn-trigger btn-icon dropdown-toggle"
                          >
                            <div className="dot dot-primary"></div>
                            <Icon name="filter-alt"></Icon>
                          </DropdownToggle>
                          <DropdownMenu right className="filter-wg dropdown-menu-xl" style={{ overflow: 'visible' }}>
                            <div className="dropdown-head">
                              <span className="sub-title dropdown-title">
                                Filter EOI Deals
                              </span>
                              <div className="dropdown">
                                <Button
                                  size="sm"
                                  className="btn-icon"
                                  onClick={resetFilters}
                                >
                                  <Icon name="undo"></Icon>
                                </Button>
                              </div>
                            </div>
                            <div className="dropdown-body dropdown-body-rg">
                              <Row className="gx-6 gy-3">
                                {/* Status Filter */}
                                <Col size="6">
                                  <div className="form-group">
                                    <Label
                                      className="overline-title overline-title-alt"
                                      htmlFor="status"
                                    >
                                      Status
                                    </Label>
                                    <RSelect
                                      options={dealStatusOptions}
                                      placeholder="Select Status"
                                      onChange={onStatusChange}
                                      value={
                                        status
                                          ? dealStatusOptions.find(
                                            (option) => option.value === status
                                          )
                                          : null
                                      }
                                      classNamePrefix="select"
                                      className="react-select-container dropdown-select"
                                      theme={(theme) => ({
                                        ...theme,
                                        colors: {
                                          ...theme.colors,
                                          primary: '#6576ff',
                                          primary25: 'rgba(101, 118, 255, 0.1)',
                                          primary50: 'rgba(101, 118, 255, 0.2)',
                                        },
                                      })}
                                    />
                                  </div>
                                </Col>

                                {/* Agent Filter */}
                                {showAgentFilter && (
                                  <Col size="6">
                                    <div className="form-group">
                                      <Label
                                        className="overline-title overline-title-alt"
                                        htmlFor="agent"
                                      >
                                        Agent
                                      </Label>
                                      <SelectAgent
                                        onSelectAgent={onAgentChange}
                                        selectedAgent={selectedAgent}
                                        classNamePrefix="select"
                                        className="react-select-container dropdown-select"
                                        theme={(theme) => ({
                                          ...theme,
                                          colors: {
                                            ...theme.colors,
                                            primary: '#6576ff',
                                            primary25: 'rgba(101, 118, 255, 0.1)',
                                            primary50: 'rgba(101, 118, 255, 0.2)',
                                          },
                                        })}
                                      />
                                    </div>
                                  </Col>
                                )}
                              </Row>

                              {/* Developer Filter - Full Width */}
                              <Row className="gy-3 mt-2">
                                <Col size="12">
                                  <div className="form-group">
                                    <Label
                                      className="overline-title overline-title-alt"
                                      htmlFor="developer"
                                    >
                                      Developer
                                    </Label>
                                    <RSelect
                                      options={developerOptions}
                                      placeholder="Select Developer"
                                      onChange={onDeveloperChange}
                                      value={selectedDeveloper}
                                      isLoading={loadingDevelopers}
                                      classNamePrefix="select"
                                      className="react-select-container dropdown-select"
                                      theme={(theme) => ({
                                        ...theme,
                                        colors: {
                                          ...theme.colors,
                                          primary: '#6576ff',
                                          primary25: 'rgba(101, 118, 255, 0.1)',
                                          primary50: 'rgba(101, 118, 255, 0.2)',
                                        },
                                      })}
                                    />
                                  </div>
                                </Col>
                              </Row>

                              {/* Project Filter - Full Width */}
                              <Row className="gy-3 mt-2">
                                <Col size="12">
                                  <div className="form-group">
                                    <Label
                                      className="overline-title overline-title-alt"
                                      htmlFor="project"
                                    >
                                      Project
                                    </Label>
                                    <RSelect
                                      options={offplanOptions}
                                      placeholder="Select Project"
                                      onChange={onOffplanChange}
                                      value={selectedOffplan}
                                      isLoading={loadingOffplans}
                                      isDisabled={!selectedDeveloper}
                                      classNamePrefix="select"
                                      className="react-select-container dropdown-select"
                                      theme={(theme) => ({
                                        ...theme,
                                        colors: {
                                          ...theme.colors,
                                          primary: '#6576ff',
                                          primary25: 'rgba(101, 118, 255, 0.1)',
                                          primary50: 'rgba(101, 118, 255, 0.2)',
                                        },
                                      })}
                                    />
                                  </div>
                                </Col>
                              </Row>

                              {/* Client Filter - Full Width */}
                              <Row className="gy-3 mt-2">
                                <Col size="12">
                                  <div className="form-group">
                                    <Label
                                      className="overline-title overline-title-alt"
                                      htmlFor="client"
                                    >
                                      Client
                                    </Label>
                                    <SearchClientSelect
                                      onChange={onClientChange}
                                      value={selectedClient}
                                      onlyUsers={true}
                                      classNamePrefix="select"
                                      className="react-select-container dropdown-select"
                                      theme={(theme) => ({
                                        ...theme,
                                        colors: {
                                          ...theme.colors,
                                          primary: '#6576ff',
                                          primary25: 'rgba(101, 118, 255, 0.1)',
                                          primary50: 'rgba(101, 118, 255, 0.2)',
                                        },
                                      })}
                                    />
                                  </div>
                                </Col>
                              </Row>

                              {/* Date Filters */}
                              <Row className="gx-6 gy-3 mt-2">
                                <Col size="6">
                                  <div className="form-group">
                                    <Label
                                      className="overline-title overline-title-alt"
                                      htmlFor="from-date"
                                    >
                                      From Date
                                    </Label>
                                    <div className="form-control-wrap">
                                      <DatePicker
                                        selected={fromDate}
                                        onChange={onFromDateChange}
                                        className="form-control"
                                        placeholderText="From Date"
                                        dateFormat="dd/MM/yyyy"
                                      />
                                    </div>
                                  </div>
                                </Col>
                                <Col size="6">
                                  <div className="form-group">
                                    <Label
                                      className="overline-title overline-title-alt"
                                      htmlFor="to-date"
                                    >
                                      To Date
                                    </Label>
                                    <div className="form-control-wrap">
                                      <DatePicker
                                        selected={toDate}
                                        onChange={onToDateChange}
                                        className="form-control"
                                        placeholderText="To Date"
                                        dateFormat="dd/MM/yyyy"
                                      />
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                            <div className="dropdown-foot d-flex justify-content-center">
                              <Button color="primary" onClick={resetFilters}>
                                Clear All Filters
                              </Button>
                            </div>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </li>
                    </ul>
                  </div>

                </div>
              </div>
              <div className="card-inner p-0">
                <table className="table table-orders">
                  <thead className="tb-odr-head">
                    <tr className="tb-odr-item">
                      <th className="tb-odr-info">
                        <span className="tb-odr-id">Deal ID</span>
                        <span className="tb-odr-date d-none d-md-inline-block">Date</span>
                      </th>
                      <th>
                        <span>Developer</span>
                      </th>
                      <th>
                        <span>Project</span>
                      </th>
                      <th>
                        <span>Agent</span>
                      </th>
                      <th>
                        <span>Client</span>
                      </th>
                      <th className="text-center">
                        <span>Status</span>
                      </th>
                      <th>&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody className="tb-odr-body">
                    {!loading &&
                      allDeals?.findDealsOutput &&
                      [...allDeals?.findDealsOutput]?.map((myDeal) => (
                        <ConvertDealProvider key={myDeal?.id}>
                          <tr className="tb-odr-item">
                            <td className="tb-odr-info bg-white">
                              <span className="tb-odr-id">
                                {
                                  [newRoles.Admin, newRoles.MasterAdmin].includes(authUser.role) && deniedPermissions?.dealDetails ? (
                                    <span>{myDeal?.reference_number}</span>
                                  ) : [newRoles.Admin, newRoles.MasterAdmin].includes(authUser.role) ? (
                                    <Link to={`/deal-details/${myDeal.id ?? myDeal._id}`}>
                                      {myDeal?.reference_number}
                                    </Link>
                                  ) : authUser.role === newRoles.TeamLeader ?
                                    (authUser?.id === myDeal?.assignedAgent?.userId) ? (
                                      <Link to={`/deal-details/${myDeal.id ?? myDeal._id}`}>
                                        {myDeal?.reference_number}
                                      </Link>
                                    ) : checkIfLeadsBelongsToAuthUser(myDeal) ? <Link to={`/deal-details/${myDeal.id ?? myDeal._id}`}>
                                      {myDeal?.reference_number}
                                    </Link> : (<span >{myDeal?.reference_number}</span>) : <Link to={`/deal-details/${myDeal.id ?? myDeal._id}`}>
                                      {myDeal?.reference_number}
                                    </Link>
                                }
                              </span>
                              <span className="tb-odr-date">
                                {new Date(myDeal?.createdAt).toLocaleDateString()}
                              </span>
                            </td>
                            <td className="bg-white">
                              <span>{myDeal?.developer?.name || "N/A"}</span>
                            </td>
                            <td className="bg-white">
                              {/* offplan is a valid field in the API response */}
                              <span>{myDeal?.offplan?.developmentName || "N/A"}</span>
                            </td>
                            <td className="bg-white">
                              <span>
                                {myDeal?.assignedAgent?.user?.first_name} {myDeal?.assignedAgent?.user?.last_name}
                              </span>
                            </td>
                            <td className="bg-white">
                              <span>
                                {myDeal?.user?.first_name} {myDeal?.user?.last_name}
                              </span>
                            </td>
                            <td className="bg-white text-center">
                              <Badge
                                color={
                                  myDeal?.status === "Approved" ||
                                    myDeal?.status === "Completed"
                                    ? "success"
                                    : myDeal?.status === DealStatus.PendingApproval || myDeal?.status === DealStatus.CollectingDocuments
                                      ? "warning"
                                      : myDeal?.status === DealStatus.Rejected || myDeal?.status === DealStatus.Canceled
                                        ? "danger"
                                        : "info"
                                }
                                pill
                                className="badge-sm badge-dot has-bg"
                              >
                                {myDeal?.status}
                              </Badge>
                            </td>
                            <td className="tb-odr-action bg-white">
                              <div className="tb-odr-btns d-none d-md-inline">
                                <Button
                                  color="primary"
                                  className="btn-sm"
                                  onClick={() => navigate(`/deal-details/${myDeal.id ?? myDeal._id}`)}
                                >
                                  View
                                </Button>
                              </div>
                              <UncontrolledDropdown className="ml-2">
                                <DropdownToggle
                                  tag="a"
                                  className="btn btn-icon btn-trigger btn-sm"
                                >
                                  <Icon name="more-h"></Icon>
                                </DropdownToggle>
                                <DropdownMenu right>
                                  <ul className="link-list-opt">
                                    <li>
                                      <DropdownItem
                                        tag="a"
                                        href="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          navigate(`/deal-details/${myDeal.id ?? myDeal._id}`);
                                        }}
                                      >
                                        <Icon name="eye"></Icon>
                                        <span>View Details</span>
                                      </DropdownItem>
                                    </li>
                                    <li>
                                      <DropdownItem
                                        tag="a"
                                        href="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          navigate(`/edit-deal/${myDeal.id ?? myDeal._id}`);
                                        }}
                                      >
                                        <Icon name="edit"></Icon>
                                        <span>Edit</span>
                                      </DropdownItem>
                                    </li>
                                  </ul>
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </td>
                          </tr>
                        </ConvertDealProvider>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="card-inner">
                {loading && <LoadingComponent />}
                {allDeals?.findDealsOutput?.length > 0 ? (
                  <PaginationComponent
                    noDown
                    currentPage={page ?? 1}
                    paginate={(page) => {
                      setPage(page);
                      queryDeals({ page: page });
                    }}
                    itemPerPage={perPage ?? 10}
                    totalItems={filCount}
                  />
                ) : (
                  <div className="text-center">
                    <span className="text-silent">No data found</span>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </Block>
      </Content>

      <ConfirmationModal
        isOpen={showConfirmModal}
        toggle={() => setShowConfirmModal(!showConfirmModal)}
        onConfirm={() => {
          // Handle confirmation action
        }}
        title="Confirm Action"
        message="Are you sure you want to perform this action?"
      />

      {showLoaderModal && <LoaderModal isOpen={showLoaderModal} text="Generating CSV..." />}
    </React.Fragment>
  );
};

export default EOIDealList;
